import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { socketService } from '../services/socket';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Stack,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Slider,
  useTheme,
  alpha,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import {
  Shield,
  VerifiedUser,
  ContentCopy,
  AutorenewOutlined,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/ui/PageHeader';
import { GlassCard } from '../components/ui/GlassCard';
import UserSelector from '../components/UserSelector';
import { AgentUser } from '../types';

enum VerificationType {
  YesNo = 0,
  Yes = 1,
  Allow = 2,
  AllowDeny = 3,
  Accept = 4,
  AcceptDecline = 5,
  Approve = 6,
  ApproveDecline = 7,
  CodeRead = 8,
  CodeEntry = 9,
}

const verificationTypeInfo = {
  [VerificationType.YesNo]: { label: 'Yes/No', description: 'User responds with Yes or No' },
  [VerificationType.Yes]: { label: 'Yes Only', description: 'User must respond Yes to verify' },
  [VerificationType.Allow]: { label: 'Allow', description: 'User allows the action' },
  [VerificationType.AllowDeny]: { label: 'Allow/Deny', description: 'User can allow or deny' },
  [VerificationType.Accept]: { label: 'Accept', description: 'User accepts the verification' },
  [VerificationType.AcceptDecline]: { label: 'Accept/Decline', description: 'User can accept or decline' },
  [VerificationType.Approve]: { label: 'Approve', description: 'User approves the action' },
  [VerificationType.ApproveDecline]: { label: 'Approve/Decline', description: 'User can approve or decline' },
  [VerificationType.CodeRead]: { label: 'Code Read', description: 'User reads the code back to you' },
  [VerificationType.CodeEntry]: { label: 'Code Entry', description: 'User enters the code themselves' },
};

const SecureVerification: React.FC = () => {
  const theme = useTheme();
  const user = useSelector((state: RootState) => state.auth.user);
  const [sending, setSending] = useState(false);

  const [verification, setVerification] = useState({
    recipient: null as AgentUser | null,
    type: VerificationType.CodeEntry,
    code: generateRandomCode(),
    message: getDefaultVerificationMessage(VerificationType.CodeEntry),
    timeout: 5, // minutes
    hasEditedMessage: false,
  });

  function generateRandomCode() {
    return Math.floor(1000 + Math.random() * 9000).toString();
  }

  function getDefaultVerificationMessage(type: VerificationType) {
    switch (type) {
      case VerificationType.CodeEntry:
        return "To verify your identity, please enter the code provided verbally.";
      case VerificationType.CodeRead:
        return "To verify your identity, please read back the code displayed on your screen.";
      case VerificationType.YesNo:
      case VerificationType.Yes:
        return "To verify your identity, please confirm the message in the affirmative.";
      case VerificationType.Allow:
      case VerificationType.AllowDeny:
        return "To verify your identity, please indicate if you allow this action.";
      case VerificationType.Accept:
      case VerificationType.AcceptDecline:
        return "To verify your identity, please accept or decline this verification request.";
      case VerificationType.Approve:
      case VerificationType.ApproveDecline:
        return "To verify your identity, please approve or decline this request.";
      default:
        return "To verify your identity, please follow the instructions provided.";
    }
  }

  const handleSendVerification = async () => {
    if (!verification.recipient) return;

    setSending(true);
    try {
      const socket = socketService.getSocket();
      if (!socket) {
        console.error('Socket not connected');
        setSending(false);
        return;
      }

      socket.emit('sendVerification', {
        recipientId: verification.recipient.agentUserId || verification.recipient.userId,
        verificationType: verification.type,
        code: verification.type === VerificationType.CodeEntry || verification.type === VerificationType.CodeRead ? verification.code : undefined,
        message: verification.message,
        timeout: verification.timeout,
      }, (response: any) => {
        if (response.success) {
          console.log('Verification sent:', response.verificationId);
          // Reset form
          setVerification({
            ...verification,
            code: generateRandomCode(),
            hasEditedMessage: false,
          });
        } else {
          console.error('Failed to send verification:', response.error);
        }
        setSending(false);
      });
    } catch (error) {
      console.error('Failed to send verification:', error);
      setSending(false);
    }
  };

  const handleVerificationTypeChange = (type: VerificationType) => {
    setVerification({
      ...verification,
      type,
      message: verification.hasEditedMessage 
        ? verification.message 
        : getDefaultVerificationMessage(type),
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    socket.on('verificationResponse', (data: any) => {
      console.log('Verification response:', data);
    });

    socket.on('verificationExpired', (data: any) => {
      console.log('Verification expired:', data);
    });

    return () => {
      socket.off('verificationResponse');
      socket.off('verificationExpired');
    };
  }, []);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <PageHeader
        title="Secure Verification"
        subtitle="Verify caller identity through secure device authentication"
        breadcrumbs={[
          { label: 'Home', href: '/dashboard' },
          { label: 'Secure Verification' },
        ]}
      />

      <Stack spacing={3}>
        <Alert severity="info" icon={<Shield />}>
          Verify a caller's identity by having them respond to a verification request on their device.
          This ensures you're speaking with the authorized user.
        </Alert>

        <GlassCard>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
            Verification Setup
          </Typography>

          <Stack spacing={3}>
            <UserSelector
              value={verification.recipient}
              onChange={(user) => setVerification({ ...verification, recipient: user })}
              label="Select User to Verify"
              required
              showCompanyFilter
            />

            <FormControl fullWidth required>
              <InputLabel>Verification Type</InputLabel>
              <Select
                value={verification.type}
                onChange={(e) => handleVerificationTypeChange(e.target.value as VerificationType)}
                label="Verification Type"
              >
                {Object.entries(verificationTypeInfo).map(([key, info]) => (
                  <MenuItem key={key} value={Number(key)}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {info.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {info.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Choose how the user will verify their identity</FormHelperText>
            </FormControl>

            {(verification.type === VerificationType.CodeEntry || 
              verification.type === VerificationType.CodeRead) && (
              <TextField
                label="Verification Code"
                value={verification.code}
                onChange={(e) => setVerification({ ...verification, code: e.target.value })}
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => copyToClipboard(verification.code)}
                        edge="end"
                      >
                        <ContentCopy />
                      </IconButton>
                      <IconButton
                        onClick={() => setVerification({ ...verification, code: generateRandomCode() })}
                        edge="end"
                      >
                        <AutorenewOutlined />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={
                  verification.type === VerificationType.CodeEntry
                    ? "User will enter this code on their device"
                    : "User will read this code back to you"
                }
              />
            )}

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Verification Timeout
              </Typography>
              <Stack spacing={2}>
                <Slider
                  value={verification.timeout}
                  onChange={(e, v) => setVerification({ ...verification, timeout: v as number })}
                  min={1}
                  max={30}
                  step={1}
                  marks={[
                    { value: 5, label: '5 min' },
                    { value: 15, label: '15 min' },
                    { value: 30, label: '30 min' },
                  ]}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 4 }}>
                  User has {verification.timeout} minutes to complete verification
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </GlassCard>

        <GlassCard>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
            Verification Message
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message to User"
            value={verification.message}
            onChange={(e) => setVerification({ 
              ...verification, 
              message: e.target.value,
              hasEditedMessage: true,
            })}
            helperText="This message will be displayed to the user on their device"
          />
        </GlassCard>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => setVerification({
              recipient: null,
              type: VerificationType.CodeEntry,
              code: generateRandomCode(),
              message: getDefaultVerificationMessage(VerificationType.CodeEntry),
              timeout: 5,
              hasEditedMessage: false,
            })}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={20} /> : <VerifiedUser />}
            onClick={handleSendVerification}
            disabled={!verification.recipient || sending}
            sx={{
              background: theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                : theme.palette.primary.main,
            }}
          >
            Send Verification Request
          </Button>
        </Box>

        {/* Verification Info Card */}
        {verification.recipient && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card sx={{ 
              background: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                  Verification Process
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2">
                    1. Send verification request to {verification.recipient.fullName}
                  </Typography>
                  <Typography variant="body2">
                    2. User receives notification on their device
                  </Typography>
                  <Typography variant="body2">
                    3. {verification.type === VerificationType.CodeEntry 
                      ? `User enters code: ${verification.code}`
                      : verification.type === VerificationType.CodeRead
                        ? `User reads code back to you`
                        : `User responds with: ${verificationTypeInfo[verification.type].label}`
                    }
                  </Typography>
                  <Typography variant="body2">
                    4. You receive confirmation of verification
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </Stack>
    </Container>
  );
};

export default SecureVerification;